import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock, Copy, Check, Sparkles, Calendar, ArrowRight, Tag } from "lucide-react";
import { toast } from "sonner";
import API from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * OfferCard — premium member-benefit card.
 *
 * - Logged-out users see the full offer details, but the promo_code is
 *   replaced with a "🔒 Sign in to reveal" CTA that preserves the return URL.
 * - Logged-in users get a "Reveal code" button. Clicking calls
 *   /api/business/offers/:id/reveal, which returns the code and logs an
 *   analytics event. Then the code becomes copy-to-clipboard.
 *
 * The card is purely presentational; the page above provides the offer.
 */
export default function OfferCard({ offer, returnUrl, testidPrefix = "offer" }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [revealed, setRevealed] = useState(null); // string | null
  const [revealing, setRevealing] = useState(false);
  const [copied, setCopied] = useState(false);

  const biz = offer?.business || {};
  const isLoggedIn = !!user;
  const testId = `${testidPrefix}-${offer.offer_id}`;
  const signInHref = `/login?next=${encodeURIComponent(returnUrl || window.location.pathname)}`;

  const reveal = async () => {
    if (!isLoggedIn) {
      window.location.href = signInHref;
      return;
    }
    setRevealing(true);
    try {
      const { data } = await API.post(`/business/offers/${offer.offer_id}/reveal`);
      setRevealed(data.promo_code);
      toast.success(t("offers.code_revealed", "Code revealed"));
    } catch (e) {
      toast.error(t("offers.reveal_failed", "Couldn't reveal the code. Please try again."));
    } finally {
      setRevealing(false);
    }
  };

  const copy = async () => {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(revealed);
      setCopied(true);
      toast.success(t("offers.code_copied", "Promo code copied"));
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error(t("offers.copy_failed", "Couldn't copy. Long-press the code instead."));
    }
  };

  const formatDate = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch {
      return null;
    }
  };
  const endsLabel = formatDate(offer.ends_at);

  return (
    <article
      className="card-surface overflow-hidden group hover:border-[#C8A24A]/45 transition-colors flex flex-col"
      data-testid={testId}
    >
      {/* Image (or gradient if none) */}
      <div className="relative aspect-[16/9] overflow-hidden">
        {offer.image_url || biz.image ? (
          <img
            src={offer.image_url || biz.image}
            alt={offer.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#1A1A20] via-[#0F1116] to-[#0A0A0F]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
        {/* Featured badge */}
        {offer.featured && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#E8D199] via-[#D4AF37] to-[#7A5F2A] text-black text-[10px] tracking-[0.22em] uppercase font-semibold px-2.5 py-1 shadow-md">
            <Sparkles className="w-3 h-3" />
            {t("offers.featured", "Featured")}
          </span>
        )}
        {/* Discount big label */}
        <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-3">
          <div>
            {biz.name && (
              <Link
                to={biz.slug ? `/marketplace/listings/${biz.slug}` : "/marketplace"}
                className="text-[10px] tracking-[0.28em] uppercase gold-text hover:underline"
                data-testid={`${testId}-business`}
              >
                {biz.name}
              </Link>
            )}
            <div className="font-display text-xl md:text-2xl text-white leading-tight max-w-[24ch]">
              {offer.title}
            </div>
          </div>
          <div
            className="shrink-0 rounded-md border border-[#C8A24A]/60 bg-black/70 backdrop-blur px-3 py-1.5 font-display text-sm md:text-base gold-text"
            data-testid={`${testId}-discount`}
          >
            {offer.discount_label}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-5 flex-1 flex flex-col">
        {offer.description && (
          <p className="text-sm text-white/72 leading-relaxed line-clamp-3" data-testid={`${testId}-description`}>
            {offer.description}
          </p>
        )}

        {/* Metadata row */}
        <div className="flex flex-wrap gap-3 mt-3 text-[11px] text-white/55">
          {endsLabel && (
            <span className="inline-flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {t("offers.valid_until", "Until")} {endsLabel}
            </span>
          )}
          {biz.city && (
            <span className="inline-flex items-center gap-1">
              <Tag className="w-3 h-3" /> {biz.city}
            </span>
          )}
        </div>

        {/* Reveal / Code area */}
        <div className="mt-5">
          {revealed ? (
            <div
              className="rounded-xl border border-[#C8A24A]/45 bg-[#0A0A10] p-3 flex items-center justify-between gap-3"
              data-testid={`${testId}-code-revealed`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-[10px] tracking-[0.28em] uppercase gold-text">
                  {t("offers.promo_code", "Promo Code")}
                </div>
                <div className="font-mono text-lg md:text-xl text-white tracking-widest truncate">
                  {revealed}
                </div>
              </div>
              <button
                onClick={copy}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-[#C8A24A]/45 bg-[#15161D] hover:bg-[#1B1C24] text-sm px-3 py-2 transition-colors"
                data-testid={`${testId}-copy`}
                aria-label={t("offers.copy_code", "Copy code")}
              >
                {copied ? <Check className="w-4 h-4 gold-text" /> : <Copy className="w-4 h-4 gold-text" />}
                <span className="gold-text">
                  {copied ? t("offers.copied", "Copied") : t("offers.copy", "Copy")}
                </span>
              </button>
            </div>
          ) : isLoggedIn ? (
            <button
              onClick={reveal}
              disabled={revealing}
              className="btn-primary w-full text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid={`${testId}-reveal-cta`}
            >
              <Sparkles className="w-4 h-4" />
              {revealing ? t("offers.revealing", "Revealing…") : t("offers.reveal_code", "Reveal promo code")}
            </button>
          ) : (
            <div className="space-y-2">
              <Link
                to={signInHref}
                className="w-full btn-primary text-sm inline-flex items-center justify-center gap-2"
                data-testid={`${testId}-signin-cta`}
              >
                <Lock className="w-4 h-4" />
                {t("offers.sign_in_to_reveal", "Sign in to reveal code")}
              </Link>
              <p className="text-[11px] text-white/45 text-center">
                {t("offers.member_benefit", "Member-only benefit — free to join.")}
              </p>
            </div>
          )}
        </div>

        {/* Terms (collapsible) */}
        {offer.terms && (
          <details className="mt-3 group/det" data-testid={`${testId}-terms`}>
            <summary className="text-[11px] text-white/55 cursor-pointer hover:text-white/75 select-none">
              {t("offers.terms", "Terms & conditions")}
            </summary>
            <p className="mt-2 text-[12px] text-white/60 leading-relaxed">{offer.terms}</p>
          </details>
        )}

        {/* Visit business */}
        {biz.slug && (
          <Link
            to={`/marketplace/listings/${biz.slug}`}
            className="mt-4 text-xs gold-text inline-flex items-center gap-1 hover:underline"
            data-testid={`${testId}-visit-business`}
          >
            {t("offers.visit_business", "Visit business")} <ArrowRight className="w-3 h-3" />
          </Link>
        )}
      </div>
    </article>
  );
}
